import { countryPreset } from './currency';
import type { DeliveryPlatform, TenantSettings } from './types';
import type { UnitSystem } from './units';

/**
 * Primeros pasos (secciones 16 y 17 del canvas de Alex, 15 oct 2026).
 *
 * Sustituyen a la «Configuración inicial» de 6 pasos y al tour de 14 globos.
 * El chat de la landing pregunta lo justo; lo demás se pone solo según el
 * país o queda como tarea en «Para empezar». La app enseña MIENTRAS la
 * persona hace cosas: tres grandes (un plato costeado, una factura, sus
 * ventas) y unas pequeñas (confirmar la comisión de cada app, revisar IVA y
 * tarjeta, descargar la app). Con las tres grandes: «Ya tienes tus números».
 *
 * Aquí solo viven las reglas puras; la web y la API las comparten.
 */

/** Por dónde eligió empezar en el chat. */
export type EmpiezaPor = 'plato' | 'factura' | 'ventas';
export const EMPIEZA_POR: readonly EmpiezaPor[] = ['plato', 'factura', 'ventas'];

export interface PrimerosPasosEstado {
  empiezaPor?: EmpiezaPor;
  /**
   * Tareas pequeñas hechas a mano: `iva-tarjeta`, `impuesto-ventas`,
   * `descarga-app`. Las grandes y las comisiones se deducen de los datos.
   */
  hechos?: string[];
  /** ISO: cuándo tuvo las tres grandes. A partir de ahí, Panel normal. */
  terminadoAt?: string;
}

export const TAREAS_PEQUENAS = ['iva-tarjeta', 'impuesto-ventas', 'descarga-app'] as const;
export type TareaPequenaId = (typeof TAREAS_PEQUENAS)[number];

/**
 * Comisión habitual de cada app de delivery, la que Alexia pone de partida
 * y marca para confirmar («¿Glovo te cobra un 30 %?»). Sin dato: 25 %.
 */
export const COMISION_HABITUAL: Record<string, number> = {
  'Uber Eats': 30,
  Glovo: 30,
  'Just Eat': 14,
  Deliveroo: 30,
  Rappi: 25,
  'DiDi Food': 25,
  DoorDash: 25,
  PedidosYa: 25,
  iFood: 23,
  'Bolt Food': 25,
  Grubhub: 25,
};

export function comisionHabitual(nombre: string): number {
  const n = nombre.trim().toLowerCase();
  const clave = Object.keys(COMISION_HABITUAL).find((k) => k.toLowerCase() === n);
  return clave ? COMISION_HABITUAL[clave] : 25;
}

/**
 * Lo que se pone solo con el país al crear la cuenta: el impuesto de las
 * ventas (México 16 %, España 10 %…) y el sistema de medidas (métrico salvo
 * EE. UU.). Un país sin preset propio deja el 10 % genérico y una tarea en
 * «Para empezar» para que ponga el suyo.
 */
export function ajustesDelPais(country: string | undefined): { vatPct: number; unitSystem: UnitSystem; impuestoPorConfirmar: boolean } {
  const preset = countryPreset(country);
  return {
    vatPct: preset.vatPct,
    unitSystem: (country ?? '').toUpperCase() === 'US' ? 'imperial' : 'metrico',
    impuestoPorConfirmar: preset.generico === true,
  };
}

/** Las apps del chat como plataformas, con su comisión habitual por confirmar. */
export function plataformasDelChat(nombres: string[], existentes: DeliveryPlatform[], idBase: string): DeliveryPlatform[] {
  const ya = new Set(existentes.map((p) => p.name.trim().toLowerCase()));
  const nuevas: DeliveryPlatform[] = [];
  for (const nombre of nombres) {
    const limpio = nombre.trim();
    if (!limpio || ya.has(limpio.toLowerCase())) continue;
    ya.add(limpio.toLowerCase());
    nuevas.push({
      platformId: `plat-${idBase}-${nuevas.length}`,
      name: limpio,
      commissionPct: comisionHabitual(limpio),
      active: true,
      commissionPending: true,
    });
  }
  return [...existentes, ...nuevas];
}

/**
 * Orden de las tres grandes: primero la que eligió en el chat y después las
 * otras en el orden plato → factura → ventas.
 */
export function ordenDeGrandes(empiezaPor: EmpiezaPor | undefined): EmpiezaPor[] {
  if (!empiezaPor) return [...EMPIEZA_POR];
  return [empiezaPor, ...EMPIEZA_POR.filter((e) => e !== empiezaPor)];
}

export interface GrandesHechas {
  plato: boolean;
  factura: boolean;
  ventas: boolean;
}

/**
 * El siguiente paso tras terminar uno: lo que falta de las tres, en el orden
 * plato → factura → ventas. Sin nada pendiente: undefined.
 */
export function siguienteGrande(hechas: GrandesHechas, recienHecha?: EmpiezaPor): EmpiezaPor | undefined {
  const marcadas = recienHecha ? { ...hechas, [recienHecha]: true } : hechas;
  return EMPIEZA_POR.find((e) => !marcadas[e]);
}

export type TareaPequena =
  | { id: string; tipo: 'comision'; platformId: string; nombre: string; pct: number }
  | { id: 'iva-tarjeta'; tipo: 'iva-tarjeta'; vatPct: number; bankCommissionPct: number }
  | { id: 'impuesto-ventas'; tipo: 'impuesto-ventas'; taxName: string }
  | { id: 'descarga-app'; tipo: 'descarga-app' };

export interface EstadoPrimerosPasos {
  grandes: Array<{ id: EmpiezaPor; hecho: boolean }>;
  hechas: number;
  /** La primera grande sin hacer, en el orden de la persona. */
  actual?: EmpiezaPor;
  pequenas: TareaPequena[];
  completo: boolean;
}

/**
 * Qué falta. Las comisiones salen de las apps con `commissionPending`; IVA y
 * tarjeta, del país (si el impuesto es genérico se pregunta el suyo); la
 * app, solo después de la primera factura (se suben mejor desde el teléfono).
 */
export function estadoPrimerosPasos(input: {
  estado: PrimerosPasosEstado | undefined;
  hechas: GrandesHechas;
  settings: Pick<TenantSettings, 'platforms' | 'vatPct' | 'bankCommissionPct'>;
  country: string | undefined;
}): EstadoPrimerosPasos {
  const orden = ordenDeGrandes(input.estado?.empiezaPor);
  const grandes = orden.map((id) => ({ id, hecho: input.hechas[id] }));
  const hechos = new Set(input.estado?.hechos ?? []);
  const pequenas: TareaPequena[] = [];
  for (const p of input.settings.platforms) {
    if (p.active && p.commissionPending) pequenas.push({ id: `comision:${p.platformId}`, tipo: 'comision', platformId: p.platformId, nombre: p.name, pct: p.commissionPct });
  }
  const preset = countryPreset(input.country);
  if (preset.generico) {
    if (!hechos.has('impuesto-ventas')) pequenas.push({ id: 'impuesto-ventas', tipo: 'impuesto-ventas', taxName: preset.taxName });
  } else if (!hechos.has('iva-tarjeta')) {
    pequenas.push({ id: 'iva-tarjeta', tipo: 'iva-tarjeta', vatPct: input.settings.vatPct, bankCommissionPct: input.settings.bankCommissionPct });
  }
  if (input.hechas.factura && !hechos.has('descarga-app')) pequenas.push({ id: 'descarga-app', tipo: 'descarga-app' });
  const hechas = grandes.filter((g) => g.hecho).length;
  return {
    grandes,
    hechas,
    actual: grandes.find((g) => !g.hecho)?.id,
    pequenas,
    completo: hechas === grandes.length,
  };
}

/** Marca una tarea pequeña como hecha (sin repetir, con tope). */
export function conTareaHecha(estado: PrimerosPasosEstado | undefined, id: string): PrimerosPasosEstado {
  const hechos = [...new Set([...(estado?.hechos ?? []), id])].slice(-40);
  return { ...estado, hechos };
}

/**
 * ¿Toca el Panel del primer día? Cuentas nuevas hasta tener las tres. Las
 * que se quedaron a medias en el cuestionario de antes (retirado) también:
 * ya no se les enseña, y lo que les faltaba son estas mismas tareas.
 */
export function primerosPasosActivos(tenant: { primerosPasos?: PrimerosPasosEstado; setup?: { completedAt?: string } } | undefined): boolean {
  if (tenant?.primerosPasos) return !tenant.primerosPasos.terminadoAt;
  return Boolean(tenant?.setup && !tenant.setup.completedAt);
}

/**
 * Avisos de la primera vez (uno por pantalla). La clave viaja a la API y se
 * guarda en la membresía: sin lista cerrada, cualquier pantalla nueva sirve,
 * pero con formato acotado.
 */
export const AVISO_ID = /^[a-z0-9][a-z0-9:-]{0,59}$/;
export const MAX_AVISOS_VISTOS = 60;

export function conAvisoVisto(vistos: string[] | undefined, id: string): string[] {
  return [...new Set([...(vistos ?? []), id])].slice(-MAX_AVISOS_VISTOS);
}
